import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import province from '../utils/province.js';

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    province: '',
    category: '',
    dateFrom: '',
    dateTo: ''
  });

  const fetchJobs = async (customFilters = filters) => {
    setLoading(true);
    try {
      const params = {};
      if (customFilters.province) params.province = customFilters.province;
      if (customFilters.category) params.category = customFilters.category;
      if (customFilters.dateFrom) params.dateFrom = customFilters.dateFrom;
      if (customFilters.dateTo) params.dateTo = customFilters.dateTo;

      const [jobsRes, categoriesRes] = await Promise.all([
        axios.get('/api/admin/jobs', { params }),
        categories.length ? Promise.resolve({ data: categories }) : axios.get('/api/categories')
      ]);

      setJobs(jobsRes.data || []);
      if (!categories.length) {
        setCategories(categoriesRes.data || []);
      }
    } catch (error) {
      console.error('Errore nel caricare le opportunità:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([axios.get('/api/admin/jobs'), axios.get('/api/categories')])
      .then(([jobsRes, categoriesRes]) => {
        setJobs(jobsRes.data || []);
        setCategories(categoriesRes.data || []);
      })
      .catch(err => console.error('Errore nel caricare i dati iniziali:', err))
      .finally(() => setLoading(false));
  }, []);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApplyFilters = () => {
    fetchJobs(filters);
  };

  const handleResetFilters = () => {
    const reset = { province: '', category: '', dateFrom: '', dateTo: '' };
    setFilters(reset);
    fetchJobs(reset);
  };

  const getJobTypeLabel = (type) => {
    const labels = {
      stage: 'Stage',
      full_time: 'Full Time',
      part_time: 'Part Time',
      freelance: 'Freelance'
    };
    return labels[type] || type || 'Altro';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Opportunità di Lavoro</Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Provincia</InputLabel>
              <Select
                label="Provincia"
                value={filters.province}
                onChange={handleFilterChange('province')}
              >
                <MenuItem value="">Tutte</MenuItem>
                {province.map((p) => (
                  <MenuItem key={p.sigla} value={p.sigla}>{p.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Categoria</InputLabel>
              <Select
                label="Categoria"
                value={filters.category}
                onChange={handleFilterChange('category')}
              >
                <MenuItem value="">Tutte</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Dal"
              type="date"
              size="small"
              value={filters.dateFrom}
              onChange={handleFilterChange('dateFrom')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              label="Al"
              type="date"
              size="small"
              value={filters.dateTo}
              onChange={handleFilterChange('dateTo')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={handleApplyFilters} fullWidth>Applica</Button>
            <Button variant="outlined" onClick={handleResetFilters} fullWidth>Reset</Button>
          </Grid>
        </Grid>
      </Paper>

      {jobs.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>Nessuna opportunità trovata.</Typography>
        </Paper>
      ) : (
        jobs.map((job) => (
          <Accordion key={job.id} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}> 
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{job.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {job.business?.name || 'Azienda'} · {new Date(job.createdAt).toLocaleDateString('it-IT')}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip label={getJobTypeLabel(job.type)} size="small" />
                  <Chip label={`${job.applications?.length || 0} candidati`} size="small" color="primary" />
                </Stack>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2">Dettagli Offerta</Typography>
                  <Typography variant="body2"><b>Luogo:</b> {job.location}</Typography>
                  <Typography variant="body2"><b>Provincia:</b> {job.province || 'N/D'}</Typography>
                  <Typography variant="body2"><b>Retribuzione:</b> {job.salary || 'N/D'}</Typography>
                  <Typography variant="body2"><b>Requisiti:</b> {job.requirements}</Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}><b>Descrizione:</b> {job.description}</Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2">Categorie</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {job.categories?.length ? job.categories.map((c) => (
                      <Chip key={`${job.id}-${c.categoryId}`} label={c.category?.name || 'N/D'} size="small" sx={{ mb: 1 }} />
                    )) : <Typography variant="body2">Nessuna categoria</Typography>}
                  </Stack>
                </Box>

                <Box>
                  <Typography variant="subtitle2">Candidati</Typography>
                  {job.applications && job.applications.length > 0 ? (
                    <List dense>
                      {job.applications.map((app) => (
                        <React.Fragment key={app.id}>
                          <ListItem alignItems="flex-start">
                            <ListItemText
                              primary={`${app.user?.fullName || 'N/D'} (${app.user?.email || 'N/D'})`}
                              secondary={
                                <>
                                  <Typography component="span" variant="body2" color="text.primary">
                                    Provincia: {app.user?.province || 'N/D'} · Stato: {app.user?.status || 'N/D'} · Disponibilità: {app.user?.availabilityStatus || 'N/D'}
                                  </Typography>
                                  <br />
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    Candidatura: {new Date(app.createdAt).toLocaleDateString('it-IT')}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                          <Divider component="li" />
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2">Nessun candidato per questa opportunità.</Typography>
                  )}
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default AdminJobs;
